// Cloudflare Turnstile widget for marketing forms (pilot signup, contact, demo).
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

type TurnstileRenderOpts = {
  sitekey: string;
  theme?: "light" | "dark" | "auto";
  size?: "normal" | "compact" | "flexible";
  appearance?: "always" | "execute" | "interaction-only";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
};

type TurnstileAPI = {
  render: (el: HTMLElement | string, opts: TurnstileRenderOpts) => string;
  reset: (id: string) => void;
  remove?: (id: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileAPI;
  }
}

/** Must match explicit programmatic rendering — see Cloudflare “Embed the widget” → explicit. */
const SCRIPT_EXPLICIT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function ensureExplicitTurnstileScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("no_window"));
    if (window.turnstile?.render) return resolve();

    const explicitScript = document.querySelector<HTMLScriptElement>(
      'script[src*="challenges.cloudflare.com/turnstile/v0/api.js"][src*="render=explicit"]',
    );

    if (explicitScript) {
      if (window.turnstile?.render) {
        resolve();
        return;
      }
      explicitScript.addEventListener("load", () => resolve(), { once: true });
      explicitScript.addEventListener("error", () => reject(new Error("script_err")), { once: true });
      return;
    }

    const s = document.createElement("script");
    s.src = SCRIPT_EXPLICIT;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("script_err"));
    document.head.appendChild(s);
  });
}

export interface UseTurnstileReturn {
  containerRef: RefObject<HTMLDivElement>;
  turnstileReady: boolean;
  token: string | undefined;
  getToken: () => string | undefined;
  reset: () => void;
  loadError: string | null;
}

export function useTurnstile(siteKey: string | null): UseTurnstileReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string | undefined>(undefined);
  const [turnstileReady, setTurnstileReady] = useState(!siteKey);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteKey) return;
    let cancelled = false;
    setLoadError(null);

    void ensureExplicitTurnstileScript()
      .then(() => {
        if (cancelled) return;

        const mountWidget = () => {
          const el = containerRef.current;
          const ts = window.turnstile;
          if (cancelled || !el || !ts?.render) {
            if (!cancelled && !ts?.render) {
              setLoadError("CAPTCHA did not load. Disable blockers for challenges.cloudflare.com.");
            }
            return;
          }

          el.innerHTML = "";

          const baseCallbacks: Pick<
            TurnstileRenderOpts,
            "callback" | "expired-callback" | "error-callback"
          > = {
            callback: (t: string) => {
              tokenRef.current = t;
              setTurnstileReady(true);
              setLoadError(null);
            },
            "expired-callback": () => {
              tokenRef.current = undefined;
              setTurnstileReady(false);
            },
            "error-callback": () => {
              tokenRef.current = undefined;
              setTurnstileReady(false);
              setLoadError(
                "CAPTCHA failed. Disable ad blockers for this site, or contact sales if the problem continues.",
              );
            },
          };

          const tryRender = (opts: TurnstileRenderOpts) => ts.render(el, opts);

          const runRender = () => {
            if (cancelled || !containerRef.current) return;
            try {
              widgetIdRef.current = tryRender({
                sitekey: siteKey,
                theme: "dark",
                size: "flexible",
                appearance: "always",
                ...baseCallbacks,
              });
            } catch (firstErr) {
              try {
                widgetIdRef.current = tryRender({
                  sitekey: siteKey,
                  theme: "dark",
                  ...baseCallbacks,
                });
              } catch {
                setLoadError(
                  "Could not start the CAPTCHA. Refresh the page or contact sales if the problem continues.",
                );
              }
            }
          };

          const schedule = () => {
            requestAnimationFrame(() => {
              if (cancelled) return;
              // ts.ready() must NOT be called after a dynamic script injection:
              // Turnstile detects its own script element is async and throws.
              // The ensureExplicitTurnstileScript Promise only resolves after
              // onload, so the API is already initialised here.
              runRender();
            });
          };

          schedule();
        };

        requestAnimationFrame(() => {
          requestAnimationFrame(mountWidget);
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError(
            "Could not load the CAPTCHA. Check your network or allow challenges.cloudflare.com.",
          );
        }
      });

    return () => {
      cancelled = true;
      const id = widgetIdRef.current;
      widgetIdRef.current = null;
      tokenRef.current = undefined;
      if (id && window.turnstile?.remove) {
        try {
          window.turnstile.remove(id);
        } catch {
          /* ignore */
        }
      }
    };
  }, [siteKey]);

  const reset = () => {
    if (siteKey && widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
    tokenRef.current = undefined;
    setTurnstileReady(!siteKey);
    setLoadError(null);
  };

  return {
    containerRef,
    turnstileReady,
    token: tokenRef.current,
    getToken: () => tokenRef.current,
    reset,
    loadError,
  };
}
